import { extractDescriptionSearchText } from '@budgie/shared';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { invalidateFinancials } from '../../lib/invalidate';
import { trpc } from '../../lib/trpc';
import { dayLabel } from './dates';
import { formatAmount } from './PurchaseRow';

export type BillSource = {
  id: string;
  description: string;
  date: string;
  amount: number;
  accountName: string | null;
};

/**
 * Turns a purchase into a recurring bill. The server creates a rule from the
 * description and links earlier purchases that match.
 */
export function CreateBillDialog({
  transaction,
  onClose,
}: {
  /** The purchase to create a bill from; null closes the dialog */
  transaction: BillSource | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const nameId = useId();
  const [name, setName] = useState('');

  useEffect(() => {
    if (transaction) {
      setName(extractDescriptionSearchText(transaction.description) || transaction.description);
    }
  }, [transaction]);

  const createBill = trpc.recurringBill.createFromTransaction.useMutation({
    onSuccess: async (result) => {
      await invalidateFinancials(utils);
      onClose();
      toast.success(
        result.matchedCount > 1
          ? `Räkningen skapades och ${result.matchedCount} köp kopplades.`
          : 'Räkningen skapades.',
      );
    },
    onError: (error) => toast.error(error.message),
  });

  const close = () => {
    if (!createBill.isPending) onClose();
  };

  const submit = () => {
    if (!transaction) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Ange ett namn för räkningen.');
      return;
    }
    createBill.mutate({ transactionId: transaction.id, name: trimmed });
  };

  return (
    <Dialog open={transaction !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-10">
          <DialogTitle className="text-[15px] font-semibold">Skapa räkning</DialogTitle>
          <DialogDescription>
            Budgie skapar en regel från köpet och kopplar tidigare köp som matchar.
          </DialogDescription>
        </DialogHeader>

        {transaction && (
          <form
            id={`${nameId}-form`}
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="flex items-start justify-between gap-3 rounded-xl bg-muted/60 p-3 text-sm">
              <div className="min-w-0">
                <p className="truncate font-medium">{transaction.description}</p>
                <p className="text-xs text-muted-foreground">
                  {dayLabel(transaction.date)} · {transaction.accountName ?? 'Okänt konto'}
                </p>
              </div>
              <span className="shrink-0 font-medium tabular-nums">
                {formatAmount(transaction.amount)}
              </span>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={nameId}>Namn på räkning</Label>
              <Input
                id={nameId}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                required
              />
            </div>
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Avbryt
          </Button>
          <Button type="submit" form={`${nameId}-form`} disabled={createBill.isPending}>
            {createBill.isPending ? 'Skapar…' : 'Skapa räkning'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
